/**
 * Brand marks — render visual logos instead of plain typed text.
 *
 * The brand IS the logo. Writing "FAITHARAMA" or "Goli" as plain serif text
 * (or worse, any random font from a body paragraph) destroys the brand
 * identity. ALWAYS use these helpers when referring to either company.
 *
 * Usage:
 *   <p>I built <Brand/> so other believers...</p>
 *   <p>Welcome to <Brand suffix=".com"/></p>
 *   <p>Promote <GoliMark/> with your audience.</p>
 *
 * Faitharama brand colors (rebranded May 2026 from wine/gold → green/orange
 * to better represent "healthy, faith + wellness" — matches Mike's logo):
 *   • FAITH:  green  #2E9F3A
 *   • ARAMA:  orange #F39A1F
 *   • .COM:   orange #F39A1F (with reduced opacity)
 */

const FAITH_GREEN  = "#2E9F3A";
const FAITH_ORANGE = "#F39A1F";

export default function Brand({ suffix = "", light = false, className = "" }) {
  // On dark backgrounds (`light` prop) we lift the green a bit so it stays legible
  const greenColor  = light ? "#5DD96A" : FAITH_GREEN;
  const orangeColor = light ? "#FFB54D" : FAITH_ORANGE;
  return (
    <span className={`inline-flex items-baseline align-baseline ${className}`} style={{ fontWeight: 800 }}>
      <span className="tracking-tight" style={{ color: greenColor }}>FAITH</span>
      <span className="tracking-tight" style={{ color: orangeColor }}>ARAMA</span>
      {suffix && <span className="text-[0.7em] tracking-[0.18em] ml-0.5 font-overline" style={{ color: orangeColor, opacity: 0.85 }}>{suffix}</span>}
    </span>
  );
}

/* ---------- Goli inline brand mark ----------
   Renders the official goli nutrition logo PNG. Two variants:
     - default: red logo (use on light backgrounds)
     - light:   black logo (use on dark backgrounds, needs a light bg behind it)

   Sizing scales with the surrounding font-size automatically.

   Usage:
     <p>Promote <GoliMark/> products to earn 20% commission.</p>
     <h2>Buy <GoliMark size="lg"/> Now</h2>
*/
const GOLI_LOGO_RED = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/xynnngrb_goli%20nutrition.png";
const GOLI_LOGO_BLACK = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/91kftdl0_goli%20nurition%20black.png";

const GOLI_SIZES = {
  inline: "h-[1.4em] w-auto",   // matches surrounding text size — for body copy
  sm:     "h-7 w-auto",
  md:     "h-10 w-auto",
  lg:     "h-16 w-auto",
  xl:     "h-24 w-auto",
};

export function GoliMark({ light = false, size = "inline", className = "" }) {
  const sizeCls = GOLI_SIZES[size] || GOLI_SIZES.inline;
  const src = light ? GOLI_LOGO_BLACK : GOLI_LOGO_RED;
  return (
    <img
      src={src}
      alt="goli nutrition"
      className={`${sizeCls} inline-block align-middle ${className}`}
      style={{ verticalAlign: "middle" }}
      loading="lazy"
    />
  );
}
